import React, { useState } from 'react'
import { Button, Card, Flex, Space, Table, TableProps, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { t } from 'i18next'
import { useAuth } from '../../auth'
import { TimeAgo } from '../../components/time-ago'
import { usePagination } from '../../hooks/use-pagination'
import { INDEX_WEBHOOK, STORE_WEBHOOK } from '../../user/permission'
import { webhookEventTypeLabel } from '../event-type'
import { useWebhooks } from '../hooks'
import { Webhook } from '../webhook'
import { CreateWebhookModal } from './create-webhook-modal'
import { Link, useNavigate } from '@tanstack/react-router'
import { searchQuery } from '../../zod/pagination'
import { z } from 'zod'

export function WebhooksList({ query }: { query?: z.infer<typeof searchQuery> }) {
    const [showCreateModal, setShowCreateModal] = useState(false)
    const { can, canAny } = useAuth()
    const canIndexWebhooks = can(INDEX_WEBHOOK)
    const tableProps = usePagination(useWebhooks, {
        enabled: canIndexWebhooks,
        query,
        search: { view: 'webhooks' },
    })

    const navigate = useNavigate()

    if (!canAny([INDEX_WEBHOOK, STORE_WEBHOOK])) {
        return null
    }

    const columns: TableProps<Webhook>['columns'] = [
        {
            title: t('Status'),
            key: 'status',
            width: 80,
            render: (record: Webhook) => (
                <Tag color={record.active ? 'green' : 'red'}>{record.active ? t('Active') : t('Inactive')}</Tag>
            ),
        },
        {
            title: t('Event'),
            dataIndex: 'eventType',
            key: 'eventType',
            width: 220,
            render: (eventType: string, record) => (
                <Link
                    to="/webhooks/$webhookId"
                    params={{ webhookId: record.id }}
                >
                    {webhookEventTypeLabel(eventType)}
                </Link>
            ),
        },
        {
            title: t('Webhook URL'),
            dataIndex: 'url',
            key: 'url',
            render: (url: string) => (
                <Typography.Text
                    className="max-w-42"
                    ellipsis={{ tooltip: url }}
                >
                    {url}
                </Typography.Text>
            ),
        },
        {
            title: t('Last Delivered'),
            dataIndex: 'lastDeliveredAt',
            key: 'lastDeliveredAt',
            width: 200,
            render: (lastDeliveredAt: Date | null) =>
                lastDeliveredAt ? <TimeAgo date={lastDeliveredAt} /> : t('Never'),
        },
        {
            title: t('Last Result'),
            key: 'latestDelivery',
            width: 150,
            render: (record: Webhook) => {
                const latestDelivery = record.latestDelivery

                if (!latestDelivery) {
                    return '-'
                }

                const tag = (
                    <Tag color={latestDelivery.status === 'success' ? 'green' : 'red'}>
                        {latestDelivery.status === 'success' ? t('Success') : t('Failed')}
                    </Tag>
                )

                if (latestDelivery.status === 'failed' && latestDelivery.errorMessage) {
                    return <Tooltip title={latestDelivery.errorMessage}>{tag}</Tooltip>
                }

                return tag
            },
        },
    ]

    return (
        <Card
            title={
                <span className="inline-flex items-center gap-2">
                    {t('Webhooks')}
                    <Tooltip
                        title={
                            <div className="space-y-1">
                                <div>
                                    {t(
                                        'Evaa adds a signature to the X-Webhook-Signature header so your server can verify that the request came from Evaa.'
                                    )}
                                </div>
                                <div>{t('The event type is sent in the X-Webhook-Event header.')}</div>
                            </div>
                        }
                    >
                        <QuestionCircleOutlined />
                    </Tooltip>
                </span>
            }
            extra={
                <Space>
                    <Button
                        type="primary"
                        disabled={!can(STORE_WEBHOOK)}
                        icon={<PlusOutlined />}
                        onClick={() => setShowCreateModal(true)}
                    >
                        {t('Create Webhook')}
                    </Button>
                </Space>
            }
        >
            <Flex
                vertical
                gap="middle"
            >
                <Typography.Text type="secondary">
                    {t(
                        'Webhooks send candidate updates to your URL. You will see a secret key once when you create the webhook.'
                    )}
                </Typography.Text>

                {canIndexWebhooks ? (
                    <Table
                        {...tableProps}
                        scroll={{ x: 900 }}
                        columns={columns}
                        rowKey="id"
                        onRow={(record) => {
                            return {
                                onClick: () => {
                                    navigate({
                                        to: '/webhooks/$webhookId',
                                        params: { webhookId: record.id },
                                    })
                                },
                            }
                        }}
                        locale={{ emptyText: t('No webhooks found') }}
                        size="small"
                    />
                ) : (
                    <Typography.Text type="secondary">
                        {t('You can create webhooks here, but you do not have permission to view existing ones.')}
                    </Typography.Text>
                )}
            </Flex>

            <CreateWebhookModal
                open={showCreateModal}
                onCancel={() => setShowCreateModal(false)}
            />
        </Card>
    )
}
